import axiosInstance from 'api/axiosInstance';
import {
  GuardLocationRaw,
  GuardLocationsResponse,
} from '../../containers/pages/CANguard/components/guardLocation/types';

/**
 * Guard Location API Client
 *
 * Handles communication with the guard location tracking backend.
 * Used by the dispatcher dashboard to show real-time guard positions.
 *
 * Backend endpoint: GET /api3.2/dispatch/guard-locations
 */

/**
 * Backend response types (different from frontend types)
 */
interface BackendCurrentAssignment {
  scheduleAssignmentId: number;
  customerName: string;
  workDate: string;
}

interface BackendGuardWithLocation {
  contactId: number;
  name: string;
  latitude: number;
  longitude: number;
  lastSeenAt: string;
  minutesAgo: number;
  batteryPercent: number | null;
  deviceInfo: string | null;
  currentAssignment: BackendCurrentAssignment | null;
}

interface BackendGuardLocationsResponse {
  guards: BackendGuardWithLocation[];
  timestamp: string;
  count: number;
}

/**
 * Query parameters for fetching guard locations
 */
export interface GetGuardLocationsParams {
  /** Filter to guards assigned to this customer */
  customerId?: number;
  /** Only return guards with recent locations (default: true) */
  activeOnly?: boolean;
  /** Define "recent" threshold in minutes (default: 120) */
  staleMinutes?: number;
}

/**
 * Helper function to determine API URL based on environment
 */
const getApiUrl = (endpoint: string): string => {
  return process.env.NODE_ENV !== 'production'
    ? `http://localhost:10000/api3.2/dispatch${endpoint}`
    : `/api3.2/dispatch${endpoint}`;
};

/**
 * Helper function to get X-Forwarded-Host header for multitenancy
 */
const getForwardedHost = (apiUrl: string): string => {
  try {
    const parsedUrl = apiUrl.startsWith('http')
      ? new URL(apiUrl)
      : new URL(apiUrl, window.location.origin);
    return parsedUrl.host;
  } catch {
    return window.location.host;
  }
};

/**
 * Transform backend response to frontend format
 *
 * The backend uses contactId and currentAssignment, while the frontend
 * components expect guardId and currentPatrol with more details.
 */
function transformGuardLocation(guard: BackendGuardWithLocation): GuardLocationRaw {
  return {
    guardId: guard.contactId,
    name: guard.name,
    latitude: guard.latitude,
    longitude: guard.longitude,
    timestamp: guard.lastSeenAt,
    minutesAgo: guard.minutesAgo,
    accuracy: null, // Not returned by dashboard endpoint
    batteryPercent: guard.batteryPercent,
    deviceInfo: guard.deviceInfo,
    currentPatrol: guard.currentAssignment
      ? {
          patrolId: guard.currentAssignment.scheduleAssignmentId,
          customerId: 0, // Not returned by backend - could be added later
          customerName: guard.currentAssignment.customerName,
          siteId: 0, // Not returned by backend - could be added later
          siteName: guard.currentAssignment.customerName, // Use customer name as fallback
          startTime: guard.currentAssignment.workDate,
        }
      : null,
  };
}

/**
 * Get all guards with their latest location
 *
 * Route: GET /api3.2/dispatch/guard-locations
 *
 * @param params - Query parameters for filtering
 * @returns Guard locations formatted for the dashboard components
 */
export async function getGuardLocations(
  params: GetGuardLocationsParams = {}
): Promise<GuardLocationsResponse> {
  const API_URL = getApiUrl('/guard-locations');

  try {
    const response = await axiosInstance.get<BackendGuardLocationsResponse>(API_URL, {
      params: {
        customerId: params.customerId,
        activeOnly: params.activeOnly ?? true,
        staleMinutes: params.staleMinutes,
      },
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });

    const data = response.data;

    // Transform backend format to frontend format
    const transformedGuards = data.guards.map(transformGuardLocation);

    return {
      guards: transformedGuards,
      timestamp: data.timestamp,
      count: data.count,
    };
  } catch (error) {
    console.error('[getGuardLocations/getGuardLocations] Failed to fetch guard locations:', error);
    throw error;
  }
}

/**
 * Get location history for a specific guard
 *
 * Route: GET /api3.2/guard/location/history/:contactId
 *
 * @param guardId - The guard's contact ID
 * @param options - Query options for filtering history
 */
export async function getGuardLocationHistory(
  guardId: number,
  options: {
    from?: string;
    to?: string;
    limit?: number;
  } = {}
): Promise<{
  contactId: number;
  locations: Array<{
    locationId: number;
    latitude: number;
    longitude: number;
    accuracy: number | null;
    recordedAt: string;
    batteryPercent: number | null;
    speed: number | null;
    altitude: number | null;
    heading: number | null;
  }>;
  count: number;
}> {
  const API_URL = getApiUrl(`/guard-location-history/${guardId}`);

  try {
    const response = await axiosInstance.get(API_URL, {
      params: {
        from: options.from,
        to: options.to,
        limit: options.limit ?? 100,
      },
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });

    const data = response.data;

    return {
      contactId: data.contactId,
      locations: data.locations.map((point: any) => ({
        locationId: point.guard_location_id,
        latitude: point.latitude,
        longitude: point.longitude,
        accuracy: point.accuracy_meters,
        recordedAt: point.recorded_at,
        batteryPercent: point.battery_percent,
        speed: point.speed_mps,
        altitude: point.altitude_meters,
        heading: point.heading,
      })),
      count: data.count,
    };
  } catch (error) {
    console.error('[getGuardLocations/getGuardLocationHistory] Failed to fetch guard location history:', error);
    throw error;
  }
}
